import React, { useEffect, useMemo, useState } from 'react';

type SizeRow = { size: number; count: number };

type Props = {
    colors: any;
    sizes?: SizeRow[]; // Keep for backward compatibility (optional)
};

// Persian digits helper (also replaces decimal and thousands separators)
const toFaDigits = (v: unknown): string => {
    if (v === null || v === undefined) return '';
    return String(v)
        .replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])
        .replace(/\./g, '٫')
        .replace(/,/g, '٬');
};

const PlateInventoryCard: React.FC<Props> = ({
                                                 colors,
                                                 sizes = []
                                             }) => {
    const [data, setData] = useState<SizeRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    // ---- styles (100% original)
    const card: React.CSSProperties = {
        background: colors.panel,
        border: `1px solid ${colors.border}`,
        borderRadius: 16,
        padding: 16,
        boxShadow: '0 6px 20px rgba(0,0,0,.25)',
    };
    const title: React.CSSProperties = { fontWeight: 800, marginBottom: 10, letterSpacing: 0.2 };
    const sub: React.CSSProperties = { fontSize: 12, color: colors.sub };

    // Normalize any plausible backend payload into [{size, count}]
    const normalize = (json: any): SizeRow[] => {
        const items = Array.isArray(json)
            ? json
            : Array.isArray(json?.items)
                ? json.items
                : Array.isArray(json?.data?.items)
                    ? json.data.items
                    : [];

        return items
            .map((r: any) => {
                const size = Number(
                    r?.size ?? r?.Size ?? r?.size_inch ?? r?.inch ?? r?.diameter ?? r?.plate_size
                );
                const count = Number(
                    r?.count ?? r?.Count ?? r?.qty ?? r?.quantity ?? r?.available ?? r?.in_stock
                );
                return { size: Number.isFinite(size) ? size : 0, count: Number.isFinite(count) ? count : 0 };
            })
            .filter((row: SizeRow) => row.size > 0);
    };

    // Use sizes → fallback to empty
    useEffect(() => {

        if (sizes.length > 0) {
            setData(normalize(sizes));
            setLoading(false);
            setErr(null);
            return;
        }

        setData([]);
        setLoading(false);
    }, [sizes]);

    // Sort largest → smallest
    const sorted = useMemo(() => [...data].sort((a, b) => b.size - a.size), [data]);

    // Determine max for percentage width
    const max = Math.max(1, ...sorted.map(d => d.count));

    // Total
    const total = useMemo(() => sorted.reduce((s, r) => s + r.count, 0), [sorted]);

    return (
        <section style={card} dir="rtl">
            <div style={title}>وضعیت موجودی اسپید</div>
            {loading && <div style={{ ...sub, color: colors.accent, marginBottom: 8 }}>در حال بارگذاری…</div>}
            {err && <div style={{ ...sub, color: colors.danger, marginBottom: 8 }}>{err}</div>}

            {!sorted.length ? (
                <div style={sub}>داده‌ای برای نمایش موجود نیست.</div>
            ) : (
                <div style={{ display: 'grid', gap: 12 }}>
                    {sorted.map((row, idx) => (
                        <div key={`${row.size}-${idx}`}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: colors.sub, marginBottom: 4 }}>
                                <span>{`${toFaDigits(row.size)} اینچ`}</span>
                                <span>{toFaDigits(row.count)}</span>
                            </div>
                            <div
                                style={{
                                    height: 10,
                                    background: '#0b1220',
                                    borderRadius: 8,
                                    overflow: 'hidden',
                                    border: `1px solid ${colors.border}`,
                                }}
                            >
                                <div
                                    style={{
                                        width: `${(row.count / max) * 100}%`,
                                        height: '100%',
                                        background: colors.accent,
                                        transition: 'width .25s ease',
                                    }}
                                />
                            </div>
                        </div>
                    ))}



                    <div style={{ ...sub, marginTop: 6, display: 'flex', justifyContent: 'space-between' }}>
                        <span>مجموع اسپید سالم در انبار</span>
                        <strong style={{ color: colors.text }}>{toFaDigits(total.toLocaleString())}</strong>
                    </div>

                    <div style={{ ...sub }}>
                        محور افقی: تعداد اسپید سالم در انبار
                    </div>
                </div>
            )}
        </section>
    );
};

export default PlateInventoryCard;